import React, { PropsWithChildren } from 'react';

import { TieTypes } from '@root/constants/collections';
import Filter from 'components/Filter';
import productsService, { CategoryDto } from 'data/products';
import visualizer from 'partials/visualizer';
import { Item, ProductCategoryFilter } from 'types';
import { capitalizeStringAsTitle } from 'utils/utils';

type FilterGroupProps = PropsWithChildren<{
  'data-testid'?: string;
  headerText: string;
}>;

const FilterGroup = ({ children, headerText, ...props }: FilterGroupProps) => (
  <div className="space-y-16" data-testid={props['data-testid']}>
    <div>
      <h3 className="text-h6">{headerText}</h3>
    </div>
    <div className="grid grid-cols-3 gap-8 xs:grid-cols-1">{children}</div>
    <hr className="border-gray-light" />
  </div>
);

export type CollectionFilterProps = {
  activeCategory: CategoryDto;
  categoryFilters: ProductCategoryFilter[];
  colorFilters: string[];
  patternFilters: string[];
  procurementFilters: string[];
  products: Item[];
  tieTypeFilters: string[];
  toggleCategoryFilters: (category: ProductCategoryFilter) => void;
  toggleColorFilters: (color: string) => void;
  togglePatternFilters: (pattern: string) => void;
  toggleProcurementFilters: (procurementType: string) => void;
  toggleTieTypeFilters: (tieTypeFilter: string) => void;
};

const CollectionFilter = (props: CollectionFilterProps) => {
  const { activeCategory } = props;

  const buildCategoryFilters = () => {
    const { categoryFilters, activeCategory, toggleCategoryFilters, products } = props;

    return productsService.categories
      .find((c) => c.name === activeCategory.name)
      ?.categoryFilters.map((filter, key) => {
        const product = products.find((p) => {
          if (p.category === 'Tie') {
            return p.display_name?.includes(filter.value as string) || filter.displayName === 'Long';
          } else {
            return p.display_name?.concat(p.category!).includes(filter.value as string);
          }
        });

        if (product) {
          return (
            <Filter
              id={`${String(filter.displayName).replace(' ', '')}FilterBtn`}
              key={key}
              onClick={() => {
                toggleCategoryFilters(filter);
              }}
              isActive={categoryFilters.includes(filter)}
              filterLabel={filter.displayName}
            />
          );
        }
      });
  };

  const buildTieTypeFilters = () => {
    const { tieTypeFilters, toggleTieTypeFilters } = props;

    // Tie Types filter should only visible under Ties category
    if (activeCategory.name !== 'Tie') {
      return null;
    }

    return TieTypes.map((filterName) => (
      <Filter
        id={`${String(filterName).replace(' ', '')}FilterBtn`}
        key={filterName}
        onClick={() => {
          toggleTieTypeFilters(filterName);
        }}
        isActive={tieTypeFilters.includes(filterName)}
        filterLabel={filterName}
      />
    ));
  };

  const buildProcurementFilters = () => {
    const { procurementFilters, toggleProcurementFilters, products } = props;

    const procurementTypes = products.map((product) =>
      product.is_retail || product.retail_bundle?.displayable || product.type?.toLowerCase() === 'purchase' ? 'Buy' : 'Rent',
    );

    const uniqueProcurementTypes = [...new Set(procurementTypes)];

    return uniqueProcurementTypes.map((filterName) => (
      <Filter
        id={`${String(filterName).replace(' ', '')}FilterBtn`}
        key={filterName}
        onClick={() => {
          toggleProcurementFilters(filterName);
        }}
        isActive={procurementFilters.includes(filterName)}
        filterLabel={filterName}
      />
    ));
  };

  const buildColorFilters = () => {
    const { colorFilters, toggleColorFilters, products } = props;

    const productColors = products.map((product) => product.color).filter((c): c is string => Boolean(c));
    const orderedProductColors = visualizer.getOrderedProductColors(productColors);

    return orderedProductColors.map((colorFilter) => (
      <Filter
        id={`${String(colorFilter).replace(' ', '')}FilterBtn`}
        key={colorFilter}
        onClick={() => {
          toggleColorFilters(colorFilter!);
        }}
        isActive={colorFilters.includes(colorFilter!)}
        filterColor={colorFilter}
        filterLabel={capitalizeStringAsTitle(colorFilter)}
      />
    ));
  };

  const buildPatternFilters = () => {
    const { activeCategory, patternFilters, togglePatternFilters, products } = props;

    if (activeCategory.name === 'tuxedos-and-suits') {
      return [];
    }

    const patterns = products.reduce(
      (patterns, product) => (product.pattern ? patterns.concat(product.pattern) : patterns),
      [] as string[],
    );

    if (patterns.length > 0) {
      return [...new Set(patterns)].map((filterName) => (
        <Filter
          id={`${filterName}FilterBtn`}
          key={filterName}
          onClick={() => {
            togglePatternFilters(filterName);
          }}
          isActive={patternFilters.includes(filterName)}
          filterLabel={filterName}
        />
      ));
    }
    return [];
  };

  const categories = activeCategory.name === '' ? [] : buildCategoryFilters();
  const colors = activeCategory.name === '' ? [] : buildColorFilters();
  const patterns = activeCategory.name === '' ? [] : buildPatternFilters();
  const procurementTypes = activeCategory.name === '' ? [] : buildProcurementFilters();
  const tieTypes = activeCategory.name === '' ? [] : buildTieTypeFilters();

  const getSelectedFilterCount = () =>
    props.categoryFilters.length +
    props.colorFilters.length +
    props.patternFilters.length +
    props.procurementFilters.length +
    props.tieTypeFilters.length;

  return (
    <div className="space-y-16">
      <div>
        <h2 className="text-h5">Filters {getSelectedFilterCount() > 0 && `(${getSelectedFilterCount()})`}</h2>
      </div>
      <hr className="border-gray-light" />

      {categories && categories.length > 0 && (
        <FilterGroup data-testid="filter-categories" headerText="Category">
          {categories}
        </FilterGroup>
      )}

      {tieTypes && tieTypes.length > 0 && (
        <FilterGroup data-testid={'filter-tie-types'} headerText="Tie Types">
          {tieTypes}
        </FilterGroup>
      )}

      {patterns.length > 0 && (
        <FilterGroup data-testid={'filter-patterns'} headerText="Pattern">
          {patterns}
        </FilterGroup>
      )}

      {colors.length > 0 && (
        <FilterGroup data-testid={'filter-colors'} headerText="Colors">
          {colors}
        </FilterGroup>
      )}

      {procurementTypes.length > 0 && (
        <FilterGroup data-testid={'filter-procurements'} headerText="Type">
          {procurementTypes}
        </FilterGroup>
      )}
    </div>
  );
};

export default CollectionFilter;
